import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crosshair, Radar, Flame, Zap, ShieldCheck, Dumbbell,
  Swords, ArrowRight, Users, Filter,
} from "lucide-react";
import { CITIES, DISCIPLINES } from "@/lib/constants";

const HERO =
  "https://images.unsplash.com/photo-1716307043003-dbe6a5cc496e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const STRIP =
  "https://images.unsplash.com/photo-1636581563867-1ecab574858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

const CTA = ({ to, children, testid, variant = "solid" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={
      variant === "solid"
        ? "inline-flex items-center gap-2 bg-volt text-black font-heading font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-sm transition-colors duration-200 hover:bg-volt-hover"
        : "inline-flex items-center gap-2 border border-[#27272a] text-white font-heading uppercase tracking-widest text-sm px-6 py-3 rounded-sm transition-colors duration-200 hover:border-volt hover:text-volt"
    }
  >
    {children}
  </Link>
);

const FEATURES = [
  { icon: Radar, title: "Sparring Radar", desc: "Scan a live feed of nearby fighters and filter by city, discipline, stance and weight until you find the perfect match." },
  { icon: Flame, title: "Camp Mode", desc: "Fight booked? Flip Camp Mode on to glow your card and float to the top of your local feed when you need work now." },
  { icon: Swords, title: "Request Rounds", desc: "Send a direct request for Hard Sparring, Technical/Flow work, or Padwork & Clinch — no awkward DMs, no time wasted." },
  { icon: ShieldCheck, title: "Fighter-First & In Control", desc: "A clean networking directory where you choose exactly who you train with — no middlemen, no gatekeepers. Full control over every connection." },
];

const STEPS = [
  { n: "01", icon: Users, title: "Build your profile", desc: "Add your gym, weight, stance, discipline and experience so partners know exactly who they're matching with." },
  { n: "02", icon: Filter, title: "Scan the radar", desc: "Filter the feed to fighters in your city and weight class who train what you train." },
  { n: "03", icon: Zap, title: "Request rounds", desc: "Fire off a match request, agree the work, and get on the mats or in the ring." },
];

export default function LandingPage() {
  return (
    <div className="relative z-10 min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#0d0d0d]/90 backdrop-blur border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Crosshair className="text-volt" size={24} strokeWidth={2.5} />
            <span className="font-heading font-black text-xl uppercase tracking-tight">Spar<span className="text-volt">Base</span></span>
          </Link>
          <div className="flex items-center gap-5 sm:gap-7">
            <Link to="/auth" data-testid="nav-login" className="hidden sm:inline-block text-sm uppercase tracking-wider text-zinc-400 hover:text-volt transition-colors duration-200">Log In</Link>
            <CTA to="/auth" testid="nav-get-started">Get Started <ArrowRight size={15} /></CTA>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#27272a]">
        <img src={HERO} alt="Fighters training in a boxing gym" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 tech-grid opacity-30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-36">
          <motion.div {...fade} className="max-w-3xl">
            <h1 className="font-heading font-extrabold uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Find your next<br /><span className="text-volt">sparring partner.</span>
            </h1>
            <p className="text-lg text-zinc-300 mt-6 max-w-xl">
              SparBase is the peer-to-peer matchmaking platform connecting Boxers,
              Muay Thai fighters, MMA and BJJ athletes across the UK. Match by gym, weight and stance —
              then request rounds.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <CTA to="/auth" testid="hero-get-started">Create Fighter Profile <ArrowRight size={16} /></CTA>
              <CTA to="/auth" testid="hero-login" variant="ghost">I have an account</CTA>
            </div>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-zinc-400">
              <span className="flex items-center gap-2"><Dumbbell size={15} className="text-volt" /> Any gym, any level</span>
              <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-volt" /> Fighter-first</span>
              <span className="flex items-center gap-2"><Zap size={15} className="text-volt" /> Free to join</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is it */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <motion.div {...fade} className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-volt text-xs uppercase tracking-[0.3em]">The problem we solve</span>
            <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tight mt-3">
              Good sparring is hard to find.
            </h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Every fighter knows the grind of chasing quality rounds — scrolling group chats,
              cold-messaging gyms, and turning up to sessions that don't match your weight or pace.
              SparBase fixes that with a single, filterable directory of active training partners
              near you.
            </p>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Whether you're deep in fight camp and need hard rounds, or a hobbyist chasing
              technical flow work, you'll find compatible partners in minutes — not weeks.
            </p>
          </div>
          <div className="relative h-72 sm:h-96 border border-[#27272a] rounded-sm overflow-hidden">
            <img src={STRIP} alt="Boxer training" className="absolute inset-0 w-full h-full object-cover grayscale-[20%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section className="border-y border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <motion.div {...fade}>
            <span className="text-volt text-xs uppercase tracking-[0.3em]">What you get</span>
            <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tight mt-3">Everything a fighter needs</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-card border border-white/10 rounded-sm p-6 hover:border-volt/50 hover:-translate-y-1 transition-transform transition-colors duration-200"
              >
                <div className="w-11 h-11 rounded-sm bg-volt/10 border border-volt/30 flex items-center justify-center">
                  <f.icon className="text-volt" size={22} />
                </div>
                <h3 className="font-heading text-xl uppercase tracking-tight mt-4">{f.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <motion.div {...fade}>
          <span className="text-volt text-xs uppercase tracking-[0.3em]">How it works</span>
          <h2 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tight mt-3">Three steps to more rounds</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
              className="relative bg-card border border-white/10 rounded-sm p-6"
            >
              <span className="font-heading font-black text-6xl text-white/5 absolute top-3 right-4">{s.n}</span>
              <s.icon className="text-volt" size={26} />
              <h3 className="font-heading text-xl uppercase tracking-tight mt-4">{s.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Disciplines + cities */}
      <section className="border-y border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <motion.div {...fade} className="text-center">
            <h2 className="font-heading font-black uppercase text-2xl sm:text-3xl tracking-tight">Every discipline. Every UK region.</h2>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {DISCIPLINES.map((d) => (
              <span key={d} className="border border-volt/40 text-volt text-sm uppercase tracking-wider px-4 py-2 rounded-sm">{d}</span>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {CITIES.map((c) => (
              <span key={c} className="border border-[#27272a] text-zinc-400 text-sm uppercase tracking-wider px-4 py-2 rounded-sm">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24 text-center">
        <motion.div {...fade}>
          <Crosshair className="text-volt mx-auto" size={36} strokeWidth={2.5} />
          <h2 className="font-heading font-extrabold uppercase text-4xl sm:text-5xl tracking-tight mt-6">
            Get on the radar<span className="text-volt">.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-lg mx-auto">
            Join fighters across the UK already using SparBase to find their next round. Free to start.
          </p>
          <div className="mt-8 flex justify-center">
            <CTA to="/auth" testid="footer-get-started">Create Your Profile <ArrowRight size={16} /></CTA>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Crosshair className="text-volt" size={20} strokeWidth={2.5} />
              <span className="font-heading font-black uppercase tracking-tight">Spar<span className="text-volt">Base</span></span>
            </Link>
            <nav className="flex items-center gap-5">
              <Link to="/terms" data-testid="footer-terms-link" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-volt transition-colors duration-200">Terms</Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-volt transition-colors duration-200">Privacy</Link>
              <span className="text-xs text-zinc-600 uppercase tracking-widest hidden sm:inline">Combat sports · UK</span>
            </nav>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed mt-6 max-w-3xl">
            SparBase is a digital networking directory only. SparBase takes zero liability for
            injuries or disputes. All users spar entirely at their own risk. Always train with
            appropriate supervision and protective equipment.
          </p>
        </div>
      </footer>
    </div>
  );
}
